import { useQuery } from '@tanstack/react-query';
import { AlertTriangle, Radar, Ban, Eye, CheckCircle, ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Badge, Card, CardHeader, CardTitle, MetricCard, PageHeader, ProductIntro, DonutChart, SectionEmptyState } from '@/components/ui';
import { api } from '@/lib/api/client';
import { unwrap } from '@/lib/api/envelope';
import { Endpoints } from '@/lib/api/endpoints';
import { cn } from '@/lib/utils/cn';
import type { ResponseEnvelope } from '@/types/api';

interface RiskSummary {
  totalEvents: number;
  allowedCount: number;
  challengeCount: number;
  reviewCount: number;
  blockedCount: number;
  lowRiskCount: number;
  mediumRiskCount: number;
  highRiskCount: number;
  criticalRiskCount: number;
  averageTrustScore: number;
  averageRiskScore: number;
}

interface DetectEvent {
  transactionId: string;
  productCode: string;
  mobileNumberMasked: string;
  operator: string;
  countryCode: string;
  riskScore: number;
  riskLevel: string;
  decision: string;
  /** Detect stores anomaly codes here. */
  reasonCodes: string[];
  /** windowStats + anomalyCodes. */
  signals?: Record<string, unknown>;
  createdAt: string;
}

interface PageDto<T> {
  content: T[];
  totalElements: number;
  totalPages: number;
  size: number;
  number: number;
}

async function fetchDetectSummary(): Promise<RiskSummary> {
  const res = await api.get<ResponseEnvelope<RiskSummary>>(Endpoints.me.riskSummary, {
    params: { productCode: 'DETECT' },
  });
  return unwrap(res);
}
async function fetchDetectEvents(): Promise<PageDto<DetectEvent>> {
  const res = await api.get<ResponseEnvelope<PageDto<DetectEvent>>>(Endpoints.me.riskEvents, {
    params: { productCode: 'DETECT', page: 0, size: 20 },
  });
  return unwrap(res);
}

const ANOMALY_LABELS: Record<string, { label: string; severity: 'high' | 'medium' }> = {
  MOBILE_RETRY_BURST:          { label: 'Mobile retry burst',        severity: 'high'   },
  DEVICE_MULTI_MOBILE:         { label: 'Device → many numbers',     severity: 'high'   },
  INSTALLATION_MULTI_MOBILE:   { label: 'Install → many numbers',    severity: 'high'   },
  IP_MULTI_MOBILE:             { label: 'IP → many numbers',         severity: 'medium' },
  OPERATOR_FAILURE_SPIKE:      { label: 'Operator failure spike',    severity: 'high'   },
  COUNTRY_FAILURE_SPIKE:       { label: 'Country failure spike',     severity: 'medium' },
  HIGH_FAILURE_RATE:           { label: 'High failure rate',         severity: 'high'   },
  RECOVERED_BUT_FAILING_AGAIN: { label: 'Recovered, failing again',  severity: 'medium' },
};

function anomalyLabel(code: string): string {
  return ANOMALY_LABELS[code]?.label ?? code.replace(/_/g, ' ').toLowerCase();
}

function riskBadgeClass(level: string) {
  switch (level) {
    case 'LOW':      return 'bg-emerald-50 text-emerald-700 dark:bg-emerald-500/15 dark:text-emerald-300';
    case 'MEDIUM':   return 'bg-amber-50 text-amber-700 dark:bg-amber-500/15 dark:text-amber-300';
    case 'HIGH':     return 'bg-orange-50 text-orange-700 dark:bg-orange-500/15 dark:text-orange-300';
    case 'CRITICAL': return 'bg-rose-50 text-rose-700 dark:bg-rose-500/15 dark:text-rose-300';
    default:         return 'bg-surface-muted text-fg-muted';
  }
}

function actionInfo(d: string): { label: string; tone: 'success' | 'warning' | 'neutral' | 'danger' } {
  switch (d) {
    case 'ALLOW':     return { label: 'Allow',     tone: 'success' };
    case 'CHALLENGE': return { label: 'Challenge', tone: 'warning' };
    case 'REVIEW':    return { label: 'Review',    tone: 'neutral' };
    case 'BLOCK':     return { label: 'Block',     tone: 'danger'  };
    default:          return { label: d,           tone: 'neutral' };
  }
}

function fmt(iso: string) {
  return new Date(iso).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });
}

export function ClientDetectPage() {
  const summaryQ = useQuery({ queryKey: ['me', 'risk-summary', 'DETECT'], queryFn: fetchDetectSummary, staleTime: 60_000 });
  const eventsQ  = useQuery({ queryKey: ['me', 'detect-events'],          queryFn: fetchDetectEvents,  staleTime: 60_000 });

  const s = summaryQ.data;
  const events = eventsQ.data?.content ?? [];

  // Count anomaly types across the loaded events for the "top anomalies" chart.
  const anomalyCounts = new Map<string, number>();
  for (const e of events) {
    for (const code of e.reasonCodes ?? []) {
      anomalyCounts.set(code, (anomalyCounts.get(code) ?? 0) + 1);
    }
  }
  const topAnomalies = [...anomalyCounts.entries()].sort((a, b) => b[1] - a[1]).slice(0, 6);
  const maxAnomaly = topAnomalies[0]?.[1] ?? 0;

  const actionSegments = s ? [
    { label: 'Allow',     value: s.allowedCount,   color: '#10b981' },
    { label: 'Challenge', value: s.challengeCount, color: '#f59e0b' },
    { label: 'Review',    value: s.reviewCount,    color: '#3b82f6' },
    { label: 'Block',     value: s.blockedCount,   color: '#ef4444' },
  ] : [];

  const flagged = s ? s.mediumRiskCount + s.highRiskCount + s.criticalRiskCount : 0;

  return (
    <div className="space-y-6 animate-fade-in">
      <PageHeader
        title="Detect"
        subtitle="Cross-event anomaly detection from Authmatech Detect."
        badge={<Badge tone="detect">Detect</Badge>}
      />

      <ProductIntro
        icon={Radar}
        tone="detect"
        eyebrow="Customer Intelligence · Detect"
        title="Catch coordinated abuse that no single-request check can see."
        points={['Cross-event detection', 'Severity-ranked anomalies', 'Early warning', 'Feeds fraud-ops']}
      >
        <p>
          <strong>Authmatech Detect</strong> looks sideways across your events — the same number retried in a
          burst, one device cycling through many numbers, an IP farm, an operator or country failure spike — to
          surface the slow-building, distributed abuse that inline checks miss because each request looks fine on
          its own.
        </p>
        <p>
          Each analysis returns severity-ranked anomalies with the affected counts behind them, so your fraud-ops
          team works the alerts that matter. Looking for a single combined view of Shield, Trust <em>and</em>
          Detect? That lives in <Link to="/portal/risk-events" className="font-semibold underline underline-offset-2">Fraud Logs</Link>.
        </p>
      </ProductIntro>

      {/* Detect hero strip */}
      <div
        className="relative overflow-hidden rounded-2xl px-6 py-5"
        style={{ background: 'linear-gradient(135deg, #7c2d12 0%, #9a3412 55%, #c2410c 100%)' }}
      >
        <div className="pointer-events-none absolute -right-6 -top-6 h-40 w-40 rounded-full bg-white/[0.04]" aria-hidden />
        <div className="relative flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-white/15">
              <Radar className="h-6 w-6 text-white" />
            </div>
            <div>
              <p className="text-xs font-semibold uppercase tracking-widest text-white/60">Total Analyses</p>
              <p className="text-4xl font-extrabold tracking-tight text-white">
                {s ? s.totalEvents.toLocaleString() : '—'}
              </p>
            </div>
          </div>
          {s && (
            <div className="flex gap-6">
              <div className="text-center">
                <p className="text-2xl font-bold text-white">{s.criticalRiskCount.toLocaleString()}</p>
                <p className="text-[11px] font-medium uppercase tracking-wide text-white/60">Critical</p>
              </div>
              <div className="text-center">
                <p className="text-2xl font-bold text-white">{s.highRiskCount.toLocaleString()}</p>
                <p className="text-[11px] font-medium uppercase tracking-wide text-white/60">High</p>
              </div>
              <div className="text-center">
                <p className="text-2xl font-bold text-white">{flagged.toLocaleString()}</p>
                <p className="text-[11px] font-medium uppercase tracking-wide text-white/60">Flagged</p>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* KPI row */}
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <MetricCard label="Analyses" value={s?.totalEvents ?? 0} icon={Radar} tone="detect" loading={summaryQ.isLoading} />
        <MetricCard label="Critical" value={s?.criticalRiskCount ?? 0} subtitle={s && s.totalEvents ? `${((s.criticalRiskCount / s.totalEvents) * 100).toFixed(1)}% of total` : undefined} icon={Ban} tone="danger" loading={summaryQ.isLoading} />
        <MetricCard label="Flagged" value={flagged} subtitle="Medium + High + Critical" icon={Eye} tone="warning" loading={summaryQ.isLoading} />
        <MetricCard label="Clean" value={s?.lowRiskCount ?? 0} subtitle={s && s.totalEvents ? `${((s.lowRiskCount / s.totalEvents) * 100).toFixed(1)}% of total` : undefined} icon={CheckCircle} tone="success" loading={summaryQ.isLoading} />
      </div>

      {/* Risk level + recommended action */}
      {s && (
        <div className="grid gap-4 lg:grid-cols-2">
          <Card>
            <CardHeader><CardTitle>Risk Level Breakdown</CardTitle></CardHeader>
            <div className="space-y-3">
              {[
                { label: 'Low',      count: s.lowRiskCount,      color: '#10b981', bg: 'from-emerald-500 to-emerald-400' },
                { label: 'Medium',   count: s.mediumRiskCount,   color: '#f59e0b', bg: 'from-amber-500 to-amber-400'     },
                { label: 'High',     count: s.highRiskCount,     color: '#f97316', bg: 'from-orange-500 to-orange-400'   },
                { label: 'Critical', count: s.criticalRiskCount, color: '#ef4444', bg: 'from-rose-500 to-rose-400'       },
              ].map(({ label, count, color, bg }) => {
                const pct = s.totalEvents ? (count / s.totalEvents) * 100 : 0;
                return (
                  <div key={label}>
                    <div className="flex items-center justify-between text-xs mb-1.5">
                      <div className="flex items-center gap-2">
                        <span className="h-2 w-2 rounded-full shrink-0" style={{ background: color }} />
                        <span className="font-medium text-fg">{label}</span>
                      </div>
                      <div className="flex items-center gap-2 text-fg-muted">
                        <span className="font-semibold text-fg tabular-nums">{count.toLocaleString()}</span>
                        <span className="text-fg-subtle">({pct.toFixed(1)}%)</span>
                      </div>
                    </div>
                    <div className="h-2 w-full overflow-hidden rounded-full bg-surface-muted">
                      <div className={`h-full rounded-full bg-gradient-to-r ${bg} transition-all duration-700`} style={{ width: `${pct}%` }} />
                    </div>
                  </div>
                );
              })}
            </div>
          </Card>

          <Card className="flex flex-col items-center">
            <CardHeader className="w-full"><CardTitle>Recommended Action</CardTitle></CardHeader>
            {actionSegments.some((d) => d.value > 0) ? (
              <DonutChart segments={actionSegments} centerValue={s.totalEvents} centerLabel="total" size={160} thickness={22} />
            ) : (
              <SectionEmptyState title="No actions yet" className="py-8" />
            )}
          </Card>
        </div>
      )}

      {/* Top anomaly types */}
      {topAnomalies.length > 0 && (
        <Card>
          <CardHeader><CardTitle>Top Anomaly Types</CardTitle></CardHeader>
          <div className="space-y-3">
            {topAnomalies.map(([code, count]) => {
              const pct = maxAnomaly ? (count / maxAnomaly) * 100 : 0;
              const sev = ANOMALY_LABELS[code]?.severity ?? 'medium';
              return (
                <div key={code}>
                  <div className="flex items-center justify-between text-xs mb-1.5">
                    <span className="font-medium text-fg">{anomalyLabel(code)}</span>
                    <span className="font-semibold text-fg tabular-nums">{count.toLocaleString()}</span>
                  </div>
                  <div className="h-2 w-full overflow-hidden rounded-full bg-surface-muted">
                    <div
                      className={cn('h-full rounded-full transition-all duration-700', sev === 'high' ? 'bg-rose-500' : 'bg-amber-500')}
                      style={{ width: `${pct}%` }}
                    />
                  </div>
                </div>
              );
            })}
          </div>
          <p className="mt-3 text-[11px] text-fg-subtle">Across the {events.length} most recent analyses.</p>
        </Card>
      )}

      {/* Recent anomaly events */}
      <Card noPadding>
        <div className="flex items-center justify-between px-5 py-4 border-b border-border">
          <CardTitle>Recent Analyses</CardTitle>
          <Link to="/portal/risk-events?productCode=DETECT" className="flex items-center gap-1 text-xs font-medium text-primary-600 hover:underline dark:text-primary-400">
            View all <ArrowRight className="h-3 w-3" />
          </Link>
        </div>
        {eventsQ.isLoading ? (
          <div className="divide-y divide-border">
            {[0, 1, 2, 3].map((i) => (
              <div key={i} className="animate-pulse flex items-center gap-3 px-5 py-3">
                <div className="h-3 w-32 rounded bg-surface-muted" />
                <div className="h-3 w-24 rounded bg-surface-muted ml-auto" />
              </div>
            ))}
          </div>
        ) : !events.length ? (
          <SectionEmptyState
            icon={Radar}
            title="No Detect analyses yet"
            description="Call the Detect API to start surfacing coordinated-abuse anomalies."
            action={
              <Link to="/portal/docs" className="text-xs font-medium text-primary-600 hover:underline dark:text-primary-400">
                View API docs →
              </Link>
            }
            className="py-12"
          />
        ) : (
          <div className="divide-y divide-border">
            {events.map((e) => {
              const { label, tone } = actionInfo(e.decision);
              return (
                <div key={e.transactionId} className="px-5 py-3">
                  <div className="flex flex-wrap items-center gap-3">
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-mono text-fg truncate">{e.transactionId}</p>
                      <p className="text-[11px] text-fg-muted">{fmt(e.createdAt)}{e.mobileNumberMasked ? ` · ${e.mobileNumberMasked}` : ''}</p>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <span className={cn('inline-block rounded-full px-2 py-0.5 text-[10px] font-semibold', riskBadgeClass(e.riskLevel))}>
                        {e.riskLevel}
                      </span>
                      <span className="text-xs font-bold tabular-nums text-fg">{e.riskScore}</span>
                      <Badge tone={tone}>{label}</Badge>
                    </div>
                  </div>
                  {e.reasonCodes?.length > 0 && (
                    <div className="mt-1.5 flex flex-wrap gap-1">
                      {e.reasonCodes.map((code) => {
                        const sev = ANOMALY_LABELS[code]?.severity ?? 'medium';
                        return (
                          <span
                            key={code}
                            className={cn(
                              'rounded-full px-2 py-0.5 text-[10px] font-medium',
                              sev === 'high'
                                ? 'bg-rose-50 text-rose-700 dark:bg-rose-500/15 dark:text-rose-300'
                                : 'bg-amber-50 text-amber-700 dark:bg-amber-500/15 dark:text-amber-300',
                            )}
                          >
                            {anomalyLabel(code)}
                          </span>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </Card>

      <div className="flex gap-3 rounded-2xl border border-amber-200 bg-amber-50 px-5 py-4 dark:border-amber-500/20 dark:bg-amber-500/10">
        <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-500" />
        <p className="text-sm text-amber-800 dark:text-amber-300">
          Detect runs across your event windows — it’s most powerful run continuously alongside an inline{' '}
          <Link to="/portal/fraud" className="font-semibold underline underline-offset-2">Shield</Link> check.
          See the full event log with reason codes and window stats in{' '}
          <Link to="/portal/risk-events?productCode=DETECT" className="font-semibold underline underline-offset-2">Fraud Logs</Link>.
        </p>
      </div>
    </div>
  );
}
